/** @jsx isolateComponent */

import React from 'react';
import { Zindexes } from '@zapier/theme';
import { css, keyframes } from '@emotion/core';
import { Animation, Colors, Shadows } from '@zapier/design-system/src/theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';
import { useClickOutside, useIntersect } from '@zapier/design-system/src/hooks';

type FloatingBoxProps = {
  /**
   * The horizontal alignment of the box relative to the parent element.
   * The vertical position is handled automatically by state.
   */
  align?: 'right' | 'left' | 'stretch';

  /**
   * What to render in the box.
   */
  children: React.ReactNode;

  /**
   * Optional callback for when a click occurs outside of `FloatingBox`.
   * Typically used to unrender `FloatingBox`.
   */
  onClickOutside?: (event: MouseEvent | TouchEvent) => void;
  /**
   * The vertical alignment of the box relative to the parent element.
   * If position is not specified, it is handled automatically by state.
   */

  position?: 'north' | 'south';
};

type DerivedState = {
  position: 'north' | 'south';
};

const menuOpenAnimation = keyframes({
  '0%': {
    transform: 'translateY(-10px)',
    opacity: 0,
  },
  // This "delay" gives enough time for the menu to be measured
  // by `useIntersect` and flip upwards if it needs to, without
  // flickering for a moment while it renders down and is measured.
  '30%': {
    transform: 'translateY(-10px)',
    opacity: 0,
  },
  '100%': {
    transform: 'translateY(0)',
    opacity: 1,
  },
});

const DISTANCE = 'calc(100% + 5px)';

const Styles = labelStyles(__filename, {
  root: (props: FloatingBoxProps, derivedState: DerivedState) => [
    css({
      animation: `${Animation.transitionDuration} ease-out ${menuOpenAnimation}`,
      position: 'absolute',
      zIndex: Zindexes.dropdownMenu,
    }),
    props.align === 'left' &&
      withModifier(props.align, {
        left: 0,
      }),
    props.align === 'right' &&
      withModifier(props.align, {
        right: 0,
      }),
    props.align === 'stretch' &&
      withModifier(props.align, {
        left: 0,
        right: 0,
      }),
    derivedState.position === 'south' &&
      withModifier(derivedState.position, {
        top: DISTANCE,
      }),
    derivedState.position === 'north' &&
      withModifier(derivedState.position, {
        bottom: DISTANCE,
      }),
  ],
  box: css({
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.grey200}`,
    borderRadius: 10,
    boxShadow: Shadows.elevation20,
  }),
});

/**
 * Box that floats and reorients itself if it renders out of the viewport.
 *
 * Tags: core-ui
 */
const FloatingBox: React.FC<FloatingBoxProps> = props => {
  const threshold = 0.9;
  const [setIntersectNode, { isBottomIntersecting }] = useIntersect({
    once: true,
    threshold,
  });

  const onClickOutside = props.onClickOutside || (() => {});

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside,
  });

  const setNode = (node: HTMLDivElement) => {
    setIntersectNode(node);
    setClickOutsideNode(node);
  };

  const derivedState = {
    position: props.position || (isBottomIntersecting ? 'north' : 'south'),
  };

  return (
    <div css={Styles.root(props, derivedState)} ref={setNode}>
      <div css={Styles.box}>{props.children}</div>
    </div>
  );
};

FloatingBox.defaultProps = {
  align: 'left',
};

export default FloatingBox;
